"use client";

import { useEffect, useRef, useState } from "react";
import classes from "./styles.module.scss";

const FADE_POSITIONS = {
  bottom: "top",
  top: "bottom",
  left: "right",
  right: "left",
};

/**
 * Fades its children in, either at first paint or once they scroll into view.
 *
 * Replaces `react-slide-fade-in`, which re-ran its effect on every render — forcing a
 * synchronous `getBoundingClientRect` and re-registering a capture-phase document scroll
 * listener each time, then never releasing it. Scroll-triggered instances now use a single
 * IntersectionObserver that disconnects once the fade has run.
 *
 * `immediate` drives the fade from CSS instead of JavaScript. Content that is above the
 * fold would otherwise be server-rendered at `opacity: 0` and stay invisible until
 * hydration finished, which delayed Largest Contentful Paint by well over a second.
 *
 * @param {Object} props
 * @param {"bottom"|"top"|"left"|"right"} [props.from]
 * @param {boolean} [props.immediate]
 * @param {number} [props.delayInMilliseconds]
 * @param {number} [props.durationInMilliseconds]
 * @param {number} [props.positionOffset]
 * @param {React.ReactNode} [props.children]
 * @returns {JSX.Element}
 */
export const FadeIn = ({
  from = "bottom",
  immediate = false,
  delayInMilliseconds = 0,
  durationInMilliseconds = 1200,
  positionOffset,
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const element = useRef(null);

  useEffect(() => {
    if (immediate) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsVisible(true);
        observer.disconnect();
      }
    });

    observer.observe(element.current);

    return () => observer.disconnect();
  }, [immediate]);

  if (immediate) {
    return (
      <div
        className={classes.fadeIn}
        style={{
          animationDuration: `${durationInMilliseconds}ms`,
          animationDelay: `${delayInMilliseconds}ms`,
        }}
      >
        {children}
      </div>
    );
  }

  const fromPosition = FADE_POSITIONS[from];

  return (
    <div
      ref={element}
      style={{
        position: "relative",
        [fromPosition]: isVisible ? 0 : positionOffset,
        opacity: isVisible ? 1 : 0,
        transition: `${fromPosition} ${durationInMilliseconds}ms, opacity ${durationInMilliseconds}ms`,
        transitionDelay: `${delayInMilliseconds}ms`,
      }}
    >
      {children}
    </div>
  );
};
